// Per-frame profiler. Each `tick()` subsystem wraps itself in mark/end calls.
// Costs are summed per frame, then averaged into a 1-second rolling window
// for display. Overhead is negligible (~2 performance.now() calls per
// section). Designed to live in the DEV cluster so any feature that pushes
// the budget over is visible the moment it happens.

export interface ProfilerStats {
  /** Average per-frame ms for each labeled section, last ~1 second. */
  perSection: Record<string, number>;
  /** Average total frame ms (whatever 'frame' label measured) last ~1 second. */
  totalMs: number;
  /** Average FPS in the window (windowMs / framesInWindow ms-per-frame). */
  fps: number;
  /** How many frames were sampled in the window. */
  frames: number;
}

/** Frame-interval histogram: 40 log buckets from 1 ms to 1 s, the last one open-ended. */
export const FRAME_BUCKETS = 40;
const BUCKET_RATIO_LOG = Math.log(1000) / FRAME_BUCKETS;
/** A frame interval longer than this counts as a long frame. */
export const LONG_FRAME_MS = 50;

/** The lower edge of bucket `i`, ms. */
export function frameBucketFloorMs(i: number): number {
  return Math.exp(i * BUCKET_RATIO_LOG);
}

export class Profiler {
  /** Frames closed since boot. */
  frameCount = 0;
  /** `performance.now()` at the first `endFrame`, or null before one. */
  firstFrameMs: number | null = null;
  /** Counts of frame intervals per bucket since the last `resetFrameHistogram`. */
  readonly frameHistogram = new Uint32Array(FRAME_BUCKETS);
  longFrames = 0;
  private lastEndMs: number | null = null;
  private firstFrameWaiters: Array<(ms: number) => void> = [];
  private starts = new Map<string, number>();
  private frame = new Map<string, number>();
  private windowAcc = new Map<string, number>();
  private framesInWindow = 0;
  private windowStartMs = performance.now();
  private stats: ProfilerStats = { perSection: {}, totalMs: 0, fps: 0, frames: 0 };

  mark(label: string): void {
    this.starts.set(label, performance.now());
  }

  end(label: string): void {
    const s = this.starts.get(label);
    if (s === undefined) return;
    const dt = performance.now() - s;
    this.frame.set(label, (this.frame.get(label) ?? 0) + dt);
    this.starts.delete(label);
  }

  /** Convenience: wrap a sync function in mark/end. */
  measure<T>(label: string, fn: () => T): T {
    this.mark(label);
    try {
      return fn();
    } finally {
      this.end(label);
    }
  }

  /** Call at the end of each frame. Accumulates per-frame costs into the
   *  rolling window; recomputes stats once per second. */
  endFrame(): void {
    for (const [k, v] of this.frame) {
      this.windowAcc.set(k, (this.windowAcc.get(k) ?? 0) + v);
    }
    this.frame.clear();
    this.framesInWindow++;

    const now = performance.now();
    this.frameCount++;
    if (this.lastEndMs !== null) {
      const dt = now - this.lastEndMs;
      const b = dt <= 1 ? 0 : Math.min(FRAME_BUCKETS - 1, Math.floor(Math.log(dt) / BUCKET_RATIO_LOG));
      this.frameHistogram[b]!++;
      if (dt > LONG_FRAME_MS) this.longFrames++;
    } else {
      this.firstFrameMs = now;
      for (const fn of this.firstFrameWaiters) fn(now);
      this.firstFrameWaiters = [];
    }
    this.lastEndMs = now;
    const windowMs = now - this.windowStartMs;
    if (windowMs >= 1000) {
      const perSection: Record<string, number> = {};
      for (const [k, v] of this.windowAcc) perSection[k] = v / this.framesInWindow;
      const totalMs = perSection['frame'] ?? 0;
      this.stats = {
        perSection,
        totalMs,
        fps: (this.framesInWindow * 1000) / windowMs,
        frames: this.framesInWindow,
      };
      this.windowAcc.clear();
      this.framesInWindow = 0;
      this.windowStartMs = now;
    }
  }

  getStats(): ProfilerStats {
    return this.stats;
  }

  resetFrameHistogram(): void {
    this.frameHistogram.fill(0);
    this.longFrames = 0;
  }

  /** Once, with `firstFrameMs`: at once when the first frame has already closed. */
  onFirstFrame(fn: (ms: number) => void): () => void {
    if (this.firstFrameMs !== null) {
      fn(this.firstFrameMs);
      return () => {};
    }
    this.firstFrameWaiters.push(fn);
    return () => { this.firstFrameWaiters = this.firstFrameWaiters.filter((w) => w !== fn); };
  }
}

export const profiler = new Profiler();
